import { Injectable, inject, NgZone } from '@angular/core';
import { SwUpdate, SwPush, VersionReadyEvent } from '@angular/service-worker';
import { Observable, Subject, fromEvent, merge, BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { LoggerService } from './logger.service';

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface SyncStatus {
  pending: number;
  lastSync: Date | null;
  isSyncing: boolean;
}

export type SWEvent = 
  | { type: 'VERSION_READY'; currentVersion: { hash: string } }
  | { type: 'UPDATE_AVAILABLE'; currentVersion: { hash: string } }
  | { type: 'VERSION_DETECTED'; currentVersion: { hash: string } }
  | { type: 'VERSION_INSTALLATION_FAILED'; currentVersion: { hash: string } }
  | { type: 'NO_NEW_VERSION_DETECTED'; currentVersion: { hash: string } };

@Injectable({ providedIn: 'root' })
export class PwaService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly swPush = inject(SwPush);
  private readonly ngZone = inject(NgZone);
  private readonly logger = inject(LoggerService);

  private readonly deferredPrompt = new Subject<BeforeInstallPromptEvent>();
  private readonly updateAvailable = new Subject<VersionReadyEvent>();
  private readonly syncStatus = new BehaviorSubject<SyncStatus>({
    pending: 0,
    lastSync: null,
    isSyncing: false,
  });

  readonly isOnline$: Observable<boolean>;
  readonly canInstall$: Observable<boolean>;
  readonly updateAvailable$: Observable<VersionReadyEvent>;
  readonly syncStatus$: Observable<SyncStatus> = this.syncStatus.asObservable();

  constructor() {
    this.isOnline$ = merge(
      fromEvent(window, 'online').pipe(map(() => true)),
      fromEvent(window, 'offline').pipe(map(() => false)),
    ).pipe(
      startWith(navigator.onLine),
      shareReplay(1),
    );

    this.canInstall$ = this.deferredPrompt.pipe(
      map(() => true),
      startWith(false),
    );

    this.updateAvailable$ = this.updateAvailable.asObservable();

    this.initInstallPrompt();
    this.initServiceWorkerUpdates();
    this.initPeriodicUpdateCheck();
    this.initSyncStatus();
  }

  private initInstallPrompt(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.logger.info('PwaService', 'Install prompt available');
      this.deferredPrompt.next(event as BeforeInstallPromptEvent);
    });

    window.addEventListener('appinstalled', () => {
      this.logger.info('PwaService', 'PWA installed successfully');
      this.deferredPrompt.complete();
    });
  }

  private initServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates.subscribe((event) => {
      if (event.type === 'VERSION_READY') {
        this.logger.info('PwaService', 'New version ready', { version: event.currentVersion.hash });
        this.updateAvailable.next(event);
      } else if (event.type === 'VERSION_DETECTED') {
        this.logger.info('PwaService', 'Update available', { version: (event as any).currentVersion?.hash });
      }
    });

    this.swUpdate.unrecoverable.subscribe((event) => {
      this.logger.error('PwaService', 'Service worker unrecoverable', { reason: event.reason });
    });
  }

  private initPeriodicUpdateCheck(): void {
    if (!this.swUpdate.isEnabled) return;

    const CHECK_INTERVAL_MS = 5 * 60 * 1000;

    setInterval(() => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        this.logger.debug('PwaService', 'Checking for updates...');
        this.swUpdate.checkForUpdate().then((updateAvailable) => {
          if (updateAvailable) {
            this.logger.info('PwaService', 'Update found via periodic check');
          }
        }).catch((err) => {
          this.logger.warn('PwaService', 'Periodic update check failed', { error: err });
        });
      }
    }, CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && navigator.onLine) {
        this.logger.debug('PwaService', 'Visibility changed, checking for updates...');
        this.swUpdate.checkForUpdate().catch((err) => {
          this.logger.warn('PwaService', 'Visibility update check failed', { error: err });
        });
      }
    });
  }

  private initSyncStatus(): void {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        const syncManager = (registration as any).sync;
        if (syncManager) {
          syncManager.getTags().then((tags: string[]) => {
            this.syncStatus.next({
              pending: tags.length,
              lastSync: new Date(),
              isSyncing: false,
            });
          });
        }
      });
    }
  }

  async promptInstall(): Promise<boolean> {
    const event = await firstValueFrom(this.deferredPrompt.pipe(filter(() => true)));
    if (!event) return false;

    await event.prompt();
    const choice = await event.userChoice;
    this.logger.info('PwaService', 'Install prompt result', { outcome: choice.outcome });
    return choice.outcome === 'accepted';
  }

  dismissInstallPrompt(): void {
    this.deferredPrompt.next(null as any);
  }

  async applyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;
    await this.swUpdate.activateUpdate();
    this.logger.info('PwaService', 'Update activated, reloading...');
    window.location.reload();
  }

  async checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) return false;
    return this.swUpdate.checkForUpdate();
  }

  registerPeriodicSync(tag: string, minInterval: number): void {
    if ('serviceWorker' in navigator && 'periodicSync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        (registration as any).periodicSync.register(tag, { minInterval });
      });
    }
  }

  registerBackgroundSync(tag: string): void {
    if ('serviceWorker' in navigator && 'sync' in window.ServiceWorkerRegistration.prototype) {
      navigator.serviceWorker.ready.then((registration) => {
        (registration as any).sync.register(tag);
        this.syncStatus.next({
          pending: this.syncStatus.value.pending + 1,
          lastSync: this.syncStatus.value.lastSync,
          isSyncing: true,
        });
      });
    }
  }

  notifySyncComplete(tag: string): void {
    this.syncStatus.next({
      pending: Math.max(0, this.syncStatus.value.pending - 1),
      lastSync: new Date(),
      isSyncing: false,
    });
  }
}

function firstValueFrom<T>(observable: Observable<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const subscription = observable.subscribe({
      next: (value) => {
        subscription.unsubscribe();
        resolve(value);
      },
      error: reject,
    });
  });
}